import { InputSignal, Signal } from '@angular/core';
import { z } from 'zod';
import { DateTime } from 'luxon';
import { formatCurrency } from '@angular/common';
import { round, sortBy } from 'lodash';

export const monthLabel: (date: DateTime) => string = (date: DateTime) => {
    return `${date.toFormat('LLL')} '${date.toFormat('yy')}`;
};

export const dailyLabel: (date: DateTime) => string = (date: DateTime) => {
    return `${date.toFormat('MM/dd/yyyy')}`;
};

export const makeHeaderRow = (id: string): HeaderRow => {
    return {
        date: {
            id: id + '-header-row-date',
            label: 'Date',
            sort: 'arrow_upward',
        },
        low: {
            id: id + '-header-row-low',
            label: 'Low',
            sort: 'none',
        },
        average: {
            id: id + '-header-row-average',
            label: 'Mid',
            sort: 'none',
        },
        high: {
            id: id + '-header-row-high',
            label: 'High',
            sort: 'none',
        },
    };
};

export const setUpRow = (
    selectedCol: { colKey: ColumnKey; col: HeaderCol },
    id,
) => {
    const headerRow = makeHeaderRow(id);
    const updatedRow: HeaderRow = updateHeaderWithColKey(
        headerRow,
        selectedCol,
    );

    return updatedRow;
};

export const updateHeaderWithColKey = (
    headerRow: HeaderRow,
    { colKey, col: selectedCol }: { colKey: ColumnKey; col: HeaderCol },
): HeaderRow => {
    return Object.entries(headerRow).reduce(
        (acc: HeaderRow, [key, col]: [ColumnKey, HeaderCol]) => {
            const sort =
                {
                    ['arrow_upward']: 'arrow_downward',
                    ['arrow_downward']: 'arrow_upward',
                    ['none']: 'arrow_upward',
                }[selectedCol.sort] ?? 'none';
            return {
                ...acc,
                [key]: {
                    ...col,
                    sort: key === colKey ? sort : 'none',
                },
            };
        },
        headerRow,
    );
};

export const sortRows = (rows: Row[], headerRow: HeaderRow): Row[] => {
    const sortKey: ColumnKey = Object.keys(headerRow).find(
        (key): boolean => headerRow[key].sort !== 'none',
    ) as ColumnKey;

    return headerRow[sortKey].sort === 'arrow_upward'
        ? sortBy(rows, (row) => row[sortKey].data).reverse()
        : sortBy(rows, (row) => row[sortKey].data);
};

export const getCurrencyLabel = (currency: number, useMiles: boolean) => {
    return useMiles
        ? formatCurrency(currency, 'en-US', '$', 'USD')
        : formatCurrency(currency, 'en-US', '$', 'USD', '1.0-0');
};

export const getMileCurrencyLabel = (
    high: any,
    mileDivider: number,
): string => {
    return getCurrencyLabel(round(high / mileDivider, 2), mileDivider !== 1);
};

export const ColumnKeySchema = z.union([
    z.literal('date'),
    z.literal('low'),
    z.literal('high'),
    z.literal('average'),
]);

export type ColumnKey = z.infer<typeof ColumnKeySchema>;

export const enum Ratecast_EquipmentCategory {
    Van = 'VAN',
    Reefer = 'REEFER',
    Flatbed = 'FLATBED',
}

export const enum Ratecast_ForecastPeriod {
    Thirtyfivedays = 'THIRTYFIVEDAYS',
    Fiftytwoweeks = 'FIFTYTWOWEEKS',
    Thirteenmonthsummary = 'THIRTEENMONTHSUMMARY',
}

export const EquipmentTypeSchema = z.union([
    z.literal('FLATBED'),
    z.literal('VAN'),
    z.literal('REEFER'),
]);
export const RateTypeSchema = z.union([
    z.literal('SPOT'),
    z.literal('CONTRACT'),
]);
export const ForecastTimeIntervalSchema = z.union([
    z.literal('THIRTYFIVEDAYS'),
    z.literal('THIRTEENMONTHSUMMARY'),
]);
export const HistoricalTimeIntervalSchema = z.literal('Monthly');
export const QuoteSchema = z.object({
    fuel: z.number(),
});

export type Quote = z.infer<typeof QuoteSchema>;

export type LaneInfo = { mileage: number };

export type EquipmentType = z.infer<typeof EquipmentTypeSchema>;
export type RateType = z.infer<typeof RateTypeSchema>;
export type ForecastTimeInterval = z.infer<typeof ForecastTimeIntervalSchema>;
export type HistoricalTimeInterval = z.infer<
    typeof HistoricalTimeIntervalSchema
>;
export const SortEnumSchema = z.union([
    z.literal('arrow_upward'),
    z.literal('arrow_downward'),
    z.literal('none'),
]);

export const HeaderColSchema = z.object({
    id: z.string(),
    label: z.string(),
    sort: SortEnumSchema,
});

export const ColSchema = z.object({
    id: z.string(),
    label: z.string(),
    data: z.union([z.date(), z.number()]),
});

export const RowSchema = z.record(ColumnKeySchema, ColSchema);

export type Col = z.infer<typeof ColSchema>;
export type Row = z.infer<typeof RowSchema>;
export type Rows = Row[];

export type HeaderCol = z.infer<typeof HeaderColSchema>;
export const HeaderRowSchema = z.record(ColumnKeySchema, HeaderColSchema);

export type HeaderRow = Required<z.infer<typeof HeaderRowSchema>>;

export interface IIqTableRateStateService {
    rowsHistorical13M(
        origin: InputSignal<string>,
        destination: InputSignal<string>,
        rateType: InputSignal<RateType>,
        equipment: InputSignal<EquipmentType>,
        quote: InputSignal<Quote>,
        useMiles: InputSignal<boolean>,
    ): Signal<Promise<Row[]>>;
    rowsForecast13M(
        origin: InputSignal<string>,
        destination: InputSignal<string>,
        rateType: InputSignal<RateType>,
        equipment: InputSignal<EquipmentType>,
        quote: InputSignal<Quote>,
        useMiles: InputSignal<boolean>,
    ): Signal<Promise<Row[]>>;
    rowsForecast35D(
        origin: InputSignal<string>,
        destination: InputSignal<string>,
        rateType: InputSignal<RateType>,
        equipment: InputSignal<EquipmentType>,
        quote: InputSignal<Quote>,
        useMiles: InputSignal<boolean>,
    ): Signal<Promise<Row[]>>;
}
